import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { getImageSrc } from "@/lib/api";
import { Trash2, Minus, Plus, ShoppingBag, Tag } from "lucide-react";

export default function Cart() {
  const { items, updateQty, removeItem, total } = useCart();
  const nav = useNavigate();

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <ShoppingBag className="w-12 h-12 mx-auto text-[#8C6D53]" />
        <h1 className="text-3xl font-extrabold text-[#1E4D3B] mt-4">Your basket is empty</h1>
        <p className="text-[#5C6660] mt-2">Explore fresh produce and livestock from Ghanaian farms.</p>
        <Link to="/marketplace" className="btn-primary px-5 py-3 rounded-xl inline-block mt-6" data-testid="cart-goto-market">Browse marketplace</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="text-3xl font-extrabold text-[#1E4D3B]">Your basket</h1>
      <div className="grid md:grid-cols-[1fr_360px] gap-8 mt-6">
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.product.id} className="card-farm p-3 flex gap-4" data-testid={`cart-item-${it.product.id}`}>
              <img src={getImageSrc(it.product)} alt={it.product.name} className="w-24 h-24 object-cover rounded-lg" />
              <div className="flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <Link to={`/product/${it.product.id}`} className="font-bold text-[#1F2421] hover:text-[#C85A32]">{it.product.name}</Link>
                    <div className="text-xs text-[#5C6660] mt-1">by {it.product.seller_name} · {it.product.region}</div>
                  </div>
                  <button onClick={() => removeItem(it.product.id)} className="p-2 text-[#D93838] hover:bg-red-50 rounded" data-testid={`cart-remove-${it.product.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <div className="flex items-center border border-[#E5D9C8] rounded-lg overflow-hidden">
                    <button onClick={() => updateQty(it.product.id, Math.max(1, it.quantity - 1))} className="p-2 hover:bg-[#F3ECE2]" data-testid={`cart-dec-${it.product.id}`}><Minus className="w-3.5 h-3.5" /></button>
                    <span className="px-3 font-bold text-sm">{it.quantity}</span>
                    <button onClick={() => updateQty(it.product.id, it.quantity + 1)} className="p-2 hover:bg-[#F3ECE2]" data-testid={`cart-inc-${it.product.id}`}><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <div className="text-right">
                    <div className="text-[#C85A32] font-black">GH₵ {((it.unit_price ?? it.product.price) * it.quantity).toFixed(2)}</div>
                    {it.unit_price && it.unit_price < it.product.price && (
                      <div className="text-[10px] inline-flex items-center gap-1 text-[#1B804E] font-bold uppercase tracking-widest" data-testid={`cart-bulk-${it.product.id}`}><Tag className="w-3 h-3" /> Bulk price</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <aside className="card-farm p-6 h-fit sticky top-24">
          <div className="text-xs uppercase tracking-widest font-bold text-[#8C6D53]">Order summary</div>
          <div className="flex justify-between mt-4 text-sm"><span>Subtotal</span><span>GH₵ {total.toFixed(2)}</span></div>
          <div className="flex justify-between mt-1 text-sm text-[#5C6660]"><span>Delivery</span><span>Arranged with seller</span></div>
          <div className="h-px bg-[#E5D9C8] my-4" />
          <div className="flex justify-between text-lg font-black text-[#1E4D3B]"><span>Total</span><span data-testid="cart-total">GH₵ {total.toFixed(2)}</span></div>
          <button onClick={() => nav("/checkout")} className="btn-primary w-full py-3 rounded-xl mt-6" data-testid="cart-checkout-button">Checkout with Mobile Money</button>
        </aside>
      </div>
    </div>
  );
}
